import { useState, useEffect } from 'react';
import './Detective.css';

// Define the interfaces
interface Error {
  id: number;
  word: string;
  correct: string;
}

interface Sentence {
  id: number;
  text: string;
  hasErrors: boolean;
  errors: Error[];
}

interface Passage {
  id: number;
  title: string;
  sentences: Sentence[];
}

interface SelectedText {
  text: string;
  start: number;
  end: number;
}

interface DetectiveProps {
  onNavigateHome: () => void;
}

// Sample incorrect passages with their corrections
const SAMPLE_PASSAGES: Passage[] = [
  {
    id: 1,
    title: "The Mystery of the Missing Apostrophe",
    sentences: [
      {
        id: 1,
        text: "The students books were left on the teachers desk.",
        hasErrors: true,
        errors: [
          { id: 1, word: "students", correct: "students'" },
          { id: 2, word: "teachers", correct: "teacher's" }
        ]
      },
      {
        id: 2,
        text: "Their going to be in trouble if they dont pick them up before the bell rings.",
        hasErrors: true,
        errors: [
          { id: 3, word: "Their", correct: "They're" },
          { id: 4, word: "dont", correct: "don't" }
        ]
      },
      {
        id: 3,
        text: "Its important to be responsible for your belongings.",
        hasErrors: true,
        errors: [
          { id: 5, word: "Its", correct: "It's" }
        ]
      }
    ]
  },
  {
    id: 2,
    title: "The Case of Confused Words",
    sentences: [
      {
        id: 1,
        text: "We were suppose to meat at the park.",
        hasErrors: true,
        errors: [
          { id: 1, word: "suppose", correct: "supposed" },
          { id: 2, word: "meat", correct: "meet" }
        ]
      },
      {
        id: 2,
        text: "She was to tired after running threw the field.",
        hasErrors: true,
        errors: [
          { id: 3, word: "to", correct: "too" },
          { id: 4, word: "threw", correct: "through" }
        ]
      },
      {
        id: 3,
        text: "I think we should of planned better, but its ok because we can try again next week.",
        hasErrors: true,
        errors: [
          { id: 5, word: "should of", correct: "should have" },
          { id: 6, word: "its", correct: "it's" }
        ]
      }
    ]
  }
];

export function Detective({ onNavigateHome }: DetectiveProps) {
  // Game state
  const [currentPassage, setCurrentPassage] = useState<Passage>(SAMPLE_PASSAGES[0]);
  const [selectedSentence, setSelectedSentence] = useState<Sentence | null>(null);
  const [currentErrorIndex, setCurrentErrorIndex] = useState<number>(0);
  const [correction, setCorrection] = useState<string>('');
  const [correctedSentences, setCorrectedSentences] = useState<{[key: number]: string}>({});
  const [score, setScore] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [gameComplete, setGameComplete] = useState<boolean>(false);
  
  // For word rearrangement
  const [isRearranging, setIsRearranging] = useState<boolean>(false);
  const [wordTiles, setWordTiles] = useState<string[]>([]);
  const [arrangedWords, setArrangedWords] = useState<string[]>([]);
  
  // Animation states
  const [showSuccessAnimation, setShowSuccessAnimation] = useState<boolean>(false);
  const [solvedSentenceId, setSolvedSentenceId] = useState<number | null>(null);
  
  // Magnifier effect (from original code)
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [showMagnifier, setShowMagnifier] = useState(false);

  // Fix potential null reference error 
  const countErrorsInPassage = () => {
    let count = 0;
    currentPassage.sentences.forEach(s => {
      if (s.errors) {
        count += s.errors.length;
      }
    });
    return count;
  };

  // Handle mouse movement to update magnifier position (from original code)
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showMagnifier) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMagnifierPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  
  // When a sentence is clicked
  const handleSentenceClick = (sentence: Sentence) => {
    // Don't allow selecting already corrected sentences
    if (correctedSentences[sentence.id]) {
      setFeedback("You've already solved this part of the case. Good work!");
      return;
    }
    
    setSelectedSentence(sentence);
    setCurrentErrorIndex(0); // Start with the first error in the sentence
    setCorrection('');
    
    // Safely check for errors
    if (sentence.errors && sentence.errors.length > 0) {
      setFeedback(`Examine this sentence carefully, Detective! Find what's wrong with: "${sentence.errors[0].word}"`);
    } else {
      setFeedback(`Examine this sentence carefully, Detective!`);
    }
  };
  
  // Submit a correction for the current error
  const handleSubmitCorrection = () => {
    if (!selectedSentence) return;
    
    // Safely access errors array and current error
    const errors = selectedSentence.errors;
    if (!errors || currentErrorIndex >= errors.length) return;
    
    const currentError = errors[currentErrorIndex];
    
    // Check if correction matches expected answer
    if (correction.trim().toLowerCase() === currentError.correct.toLowerCase()) {
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 800);
      
      setFeedback("Great detective work! That's the right correction.");
      setScore(score + 10);
      
      // Move to next error in sentence or rearrangement phase
      if (currentErrorIndex < selectedSentence.errors.length - 1) {
        setCurrentErrorIndex(currentErrorIndex + 1);
        setCorrection('');
        
        // Update feedback for next error
        setTimeout(() => {
          if (selectedSentence && selectedSentence.errors && selectedSentence.errors.length > currentErrorIndex + 1) {
            setFeedback(`Now fix this error: "${selectedSentence.errors[currentErrorIndex + 1].word}"`);
          } else {
            setFeedback(`Continue your excellent detective work!`);
          }
        }, 1000);
      } else {
        // All errors in sentence corrected, move to word rearrangement
        setTimeout(() => {
          prepareWordRearrangement();
        }, 1000);
      }
    } else {
      setFeedback(`That's not quite right. Try again, Detective!`);
      setScore(Math.max(0, score - 2)); // Small penalty, but never below zero
    }
  };
  
  // Prepare the word rearrangement phase
  const prepareWordRearrangement = () => {
    if (!selectedSentence) return;
    
    // Create a corrected version of the sentence
    let correctedText = selectedSentence.text;
    
    // Safely check for errors array
    if (selectedSentence.errors) {
      selectedSentence.errors.forEach(error => {
        // Need to be careful with word boundaries
        const regex = new RegExp(`\\b${error.word}\\b`, 'g');
        correctedText = correctedText.replace(regex, error.correct);
      });
    }
    
    // Split into words and shuffle slightly (don't make it too hard)
    const words = correctedText.split(/\s+/);
    const shuffled = shuffleWords(words);
    
    setWordTiles(shuffled);
    setArrangedWords([]);
    setIsRearranging(true);
    setFeedback("Now rearrange the words to reconstruct the correct sentence!");
  };
  
  // Simple shuffle algorithm that keeps some word order intact for easier puzzles
  const shuffleWords = (words: string[]) => {
    // For beginners: just swap a few pairs of words
    // This creates a puzzle but doesn't make it frustratingly difficult
    const shuffled = [...words];
    for (let i = 0; i < Math.min(3, Math.floor(words.length / 2)); i++) {
      const idx1 = Math.floor(Math.random() * words.length);
      const idx2 = Math.floor(Math.random() * words.length);
      [shuffled[idx1], shuffled[idx2]] = [shuffled[idx2], shuffled[idx1]];
    }
    return shuffled;
  };
  
  // Handle selecting a word from the word bank
  const handleSelectWord = (index: number) => {
    // Move the word from tiles to arranged area
    const newTiles = [...wordTiles];
    const word = newTiles.splice(index, 1)[0];
    
    setWordTiles(newTiles);
    setArrangedWords([...arrangedWords, word]);
  };
  
  // Handle removing a word from the arranged area (put back in word bank)
  const handleRemoveWord = (index: number) => {
    const newArranged = [...arrangedWords];
    const word = newArranged.splice(index, 1)[0];
    
    setArrangedWords(newArranged);
    setWordTiles([...wordTiles, word]);
  };
  
  // Submit the rearranged sentence
  const handleSubmitRearrangement = () => {
    if (!selectedSentence) return;
    
    // Create a corrected version of the sentence for comparison
    let correctedText = selectedSentence.text;
    
    // Safely check for errors array
    if (selectedSentence.errors) {
      selectedSentence.errors.forEach(error => {
        // Need to be careful with word boundaries
        const regex = new RegExp(`\\b${error.word}\\b`, 'g');
        correctedText = correctedText.replace(regex, error.correct);
      });
    }
    
    // Get the words in correct order for comparison (ignoring case and punctuation for flexibility)
    const correctWords = correctedText.toLowerCase().replace(/[.,!?;:'"()]/g, '').split(/\s+/);
    const submittedWords = arrangedWords.join(' ').toLowerCase().replace(/[.,!?;:'"()]/g, '').split(/\s+/);
    
    // Check if rearrangement is correct (allowing for some flexibility)
    const isCorrect = correctWords.length === submittedWords.length && 
      correctWords.every((word, i) => word === submittedWords[i]);
    
    if (isCorrect) {
      // Show animation for the solved sentence
      setSolvedSentenceId(selectedSentence.id);
      setTimeout(() => setSolvedSentenceId(null), 1500);
      
      setFeedback("Perfect! You've successfully reconstructed the sentence!");
      setScore(score + 15); // Bonus for sentence reconstruction
      
      // Save the corrected sentence (with proper case and punctuation from original)
      let finalSentence = "";
      
      // Reconstruct with proper capitalization and ending punctuation
      const endPunctuationMatch = correctedText.match(/[.!?]$/);
      if (endPunctuationMatch && endPunctuationMatch[0]) {
        const endPunctuation = endPunctuationMatch[0];
        finalSentence = arrangedWords.join(' ');
        
        // Ensure first letter is capitalized
        finalSentence = finalSentence.charAt(0).toUpperCase() + finalSentence.slice(1);
        
        // Ensure ending punctuation
        if (!finalSentence.match(/[.!?]$/)) {
          finalSentence += endPunctuation;
        }
      } else {
        finalSentence = arrangedWords.join(' ');
        // Ensure first letter is capitalized
        finalSentence = finalSentence.charAt(0).toUpperCase() + finalSentence.slice(1);
      }
      
      setCorrectedSentences({
        ...correctedSentences,
        [selectedSentence.id]: finalSentence
      });
      
      // Reset for next sentence
      setTimeout(() => {
        setSelectedSentence(null);
        setIsRearranging(false);
        
        // Check if all sentences are corrected
        const allCorrected = currentPassage.sentences.every(
          sentence => correctedSentences[sentence.id] || (selectedSentence && sentence.id === selectedSentence.id)
        );
        
        if (allCorrected) {
          setGameComplete(true);
          setFeedback(`Case closed! You've solved the entire passage! Final score: ${score + 15}`);
        }
      }, 1500);
    } else {
      setFeedback("The sentence doesn't look quite right. Try a different arrangement!");
      // Optionally reduce score or provide hints
    }
  };
  
  // Reset the game with a new passage
  const resetGame = () => {
    const nextPassageIndex = SAMPLE_PASSAGES.findIndex(p => p.id === currentPassage.id) + 1;
    if (nextPassageIndex < SAMPLE_PASSAGES.length) {
      setCurrentPassage(SAMPLE_PASSAGES[nextPassageIndex]);
    } else {
      setCurrentPassage(SAMPLE_PASSAGES[0]);
    }
    
    setSelectedSentence(null);
    setIsRearranging(false);
    setCorrectedSentences({});
    setFeedback('');
    setGameComplete(false);
    setScore(0);
  };

  return (
    <div className="detective-game">
      {/* Detective Header - Comic Book Style */}
      <header className="detective-header">
        <div className="header-glow-1"></div>
        <div className="header-glow-2"></div>
        
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center">
            <div className="detective-logo">🔍</div>
            <div>
              <h1 className="detective-title">GRAMMAR DETECTIVE</h1>
              <div className="detective-subtitle">Can you solve the case?</div>
            </div>
          </div>
          <div className="score-badge">
            <span className="mr-2">⭐</span>
            <span>SCORE: {score}</span>
          </div>
        </div>
        <p className="detective-subtitle">
          Find grammar errors, correct them, and rebuild the sentences to crack the case!
        </p>
      </header>

      {/* Case File - Comic Book Panel Style */}
      <div className="case-file">
        {/* Case Banner */}
        <div className="case-banner">
          <div className="case-banner-text">
            CASE #{currentPassage.id}: {currentPassage.title}
          </div>
        </div>
        
        {/* Progress */}
        <div className="mb-6">
          <p className="progress-label">
            <span className="progress-icon">📋</span>
            CASE PROGRESS: {Object.keys(correctedSentences).length}/{currentPassage.sentences.length} SENTENCES SOLVED
          </p>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ width: `${(Object.keys(correctedSentences).length / currentPassage.sentences.length) * 100}%` }}
            ></div>
          </div>
        </div>
        
        {/* Passage with clickable sentences - Comic Panel Style */}
        <div 
          className="passage-panel"
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setShowMagnifier(true)}
          onMouseLeave={() => setShowMagnifier(false)}
        >
          {currentPassage.sentences.map(sentence => {
            // Determine sentence class based on state
            let sentenceClass = "sentence sentence-default";
            if (correctedSentences[sentence.id]) {
              sentenceClass = "sentence sentence-solved";
            } else if (selectedSentence && selectedSentence.id === sentence.id) {
              sentenceClass = "sentence sentence-selected";
            }
            
            if (solvedSentenceId === sentence.id) {
              sentenceClass += " animate-pulse";
            }
            
            return (
              <div 
                key={sentence.id}
                className={sentenceClass}
                onClick={() => !isRearranging && handleSentenceClick(sentence)}
              >
                {correctedSentences[sentence.id] ? (
                  <div className="flex items-center">
                    <div className="check-mark">✓</div>
                    <span>{correctedSentences[sentence.id]}</span>
                  </div>
                ) : (
                  <div className="flex items-start">
                    <div className="sentence-number">
                      {sentence.id}
                    </div>
                    <span>{sentence.text}</span>
                  </div>
                )}
                
                {/* Show success animation */}
                {showSuccessAnimation && selectedSentence && selectedSentence.id === sentence.id && (
                  <div className="success-animation">
                    <div className="success-icon">
                      ✓
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Magnifying glass effect */}
          {showMagnifier && !isRearranging && (
            <div 
              className="magnifying-glass"
              style={{ 
                left: `${magnifierPosition.x - 40}px`, 
                top: `${magnifierPosition.y - 40}px`
              }}
            ></div>
          )}
        </div>
        
        {/* Instructions if nothing is selected */}
        {!selectedSentence && !gameComplete && !isRearranging && (
          <div className="instructions-panel">
            <h3 className="instructions-title">
              <span className="instructions-icon">📝</span>
              DETECTIVE NOTES
            </h3>
            <p>Click on a sentence to begin your investigation. Look carefully for grammatical errors, Detective!</p>
          </div>
        )}
        
        {/* Error Correction UI - Comic Speech Bubble Style */}
        {selectedSentence && !isRearranging && !gameComplete && selectedSentence.errors && selectedSentence.errors.length > currentErrorIndex && (
          <div className="correction-panel">
            <div className="correction-glow"></div>
            
            <h3 className="correction-title">
              <span className="correction-icon">🔎</span>
              DETECTIVE WORK
            </h3>
            <p className="mb-4">
              Find and correct this error: 
              <span className="error-word">
                {selectedSentence.errors[currentErrorIndex].word}
              </span>
            </p>
            
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label htmlFor="correction" className="block text-sm font-medium text-indigo-300 mb-1">
                  YOUR CORRECTION:
                </label>
                <input
                  type="text"
                  id="correction"
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  className="correction-input"
                  placeholder={`Fix "${selectedSentence.errors[currentErrorIndex].word}"`}
                />
              </div>
              <button
                onClick={handleSubmitCorrection}
                className="button-primary"
              >
                SUBMIT
              </button>
            </div>
            
            <div className="correction-counter">
              <div className="counter-badge">
                #{currentErrorIndex + 1}/{selectedSentence.errors ? selectedSentence.errors.length : 0}
              </div>
              <p>Find all errors to continue</p>
            </div>
          </div>
        )}
        
        {/* Word Rearrangement UI - Comic Page Style */}
        {isRearranging && !gameComplete && (
          <div className="rearrangement-panel">
            <h3 className="rearrangement-title">
              <span className="rearrangement-icon">🧩</span>
              SENTENCE RECONSTRUCTION
            </h3>
            <p className="mb-4">
              Rearrange these words to create a grammatically correct sentence:
            </p>
            
            {/* Arranged Words Area */}
            <div className="arranged-words-area">
              <div className="flex flex-wrap gap-2">
                {arrangedWords.length === 0 ? (
                  <p className="text-slate-500 italic">Click words from below to arrange them here...</p>
                ) : (
                  arrangedWords.map((word, index) => (
                    <div 
                      key={index}
                      className="arranged-word"
                      onClick={() => handleRemoveWord(index)}
                    >
                      {word}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Word Bank */}
            <div className="word-bank">
              <p className="word-bank-title">
                <span className="mr-2">📚</span>
                WORD BANK:
              </p>
              <div className="flex flex-wrap gap-2">
                {wordTiles.map((word, index) => (
                  <div 
                    key={index}
                    className="word-tile"
                    onClick={() => handleSelectWord(index)}
                  >
                    {word}
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={handleSubmitRearrangement}
              disabled={wordTiles.length > 0} // All words must be used
              className="submit-rearrangement"
            >
              SUBMIT SENTENCE
            </button>
          </div>
        )}
        
        {/* Feedback - Comic Speech Bubble */}
        {feedback && (
          <div className={`feedback-panel ${gameComplete ? 'feedback-panel--success' : 'feedback-panel--info'}`}>
            <div className="feedback-overlay"></div>
            <div className="feedback-content">
              <span className="feedback-icon">
                {gameComplete ? '🏆' : '💡'}
              </span>
              <div>{feedback}</div>
            </div>
          </div>
        )}
        
        {/* Case Complete UI - Comic Book Victory Page */}
        {gameComplete && (
          <div className="case-complete">
            <div className="complete-glow"></div>
            
            <h2 className="complete-title">
              <span className="trophy-icon">🏆</span>
              CASE SOLVED!
            </h2>
            
            <div className="mb-5">
              <h3 className="evidence-title">Corrected Evidence:</h3>
              <div className="evidence-container">
                {currentPassage.sentences.map(sentence => (
                  <p key={sentence.id} className="mb-3">
                    {correctedSentences[sentence.id]}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="final-score">
              <div className="score-glow"></div>
              <div className="relative z-10">
                <p className="score-value">
                  FINAL SCORE: {score} POINTS
                </p>
                <p className="score-detail">
                  You've successfully corrected {countErrorsInPassage()} grammatical errors!
                </p>
                <p className="score-message">
                  Your detective skills are impressive. Ready for another challenge?
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Game Controls */}
      <div className="game-controls">
        <button
          onClick={onNavigateHome}
          className="button-secondary"
        >
          <span className="button-icon">←</span>
          BACK TO HQ
        </button>
        
        <button
          onClick={resetGame}
          className="button-action"
        >
          <span className="button-icon">🔄</span>
          {gameComplete ? "NEW CASE" : "SKIP CASE"}
        </button>
      </div>
    </div>
  );
}

export default Detective;